import { useState } from 'react';
import { Box, Button, Flex, Text, Textarea } from '@chakra-ui/react';

import { accent, fontSize, neutral, text } from 'styles/revampTokens';

import { taskFieldProperties, taskPrimaryButtonProperties } from './taskControls';
import { formatWhen } from './format';
import { TasksViewer } from './TasksChrome';
import { ActivityEvent, TaskComment } from './types';

const ACTIVITY_LABEL: Record<string, string> = {
  'task.created': 'Task issued',
  'task.inputs_changed': 'Plan updated',
  'task.submitted': 'Submitted',
  'task.reopened': 'Sent back',
  'task.reassigned': 'Reassigned',
  'task.approved': 'Approved',
  'task.closed': 'Closed',
  'task.commented': 'Comment added',
  'task.due_changed': 'Due date changed',
};

const personName = (
  userId: number | null,
  viewer: TasksViewer | undefined,
  people: Record<number, string>,
) => {
  if (userId == null) return 'Blox';
  if (viewer?.userId != null && userId === viewer.userId) return 'You';
  return people[userId] ?? 'A teammate';
};

const commentControlLabel = (count: number) => {
  if (count === 1) return '1 comment';
  if (count > 1) return `${count} comments`;
  return 'Comments';
};

/** Same 32px row and 12px type as the step tabs, so the labels share a baseline. */
const stripButton = (active: boolean) => ({
  align: 'center' as const,
  h: '32px',
  px: '12px',
  flexShrink: 0,
  borderRadius: '6px',
  color: active ? accent.solid : text.secondary,
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '1',
  bg: active ? neutral.surfaceHover : 'transparent',
});

export const ConversationToggles = ({
  count,
  panel,
  onPanel,
}: {
  count: number;
  panel: 'comments' | 'activity' | null;
  onPanel: (next: 'comments' | 'activity') => void;
}) => (
  <Flex gap='4px' ml='8px' h='32px' flexShrink={0} align='center'>
    <Flex
      as='button'
      type='button'
      aria-pressed={panel === 'comments'}
      aria-label={commentControlLabel(count)}
      onClick={() => onPanel('comments')}
      {...stripButton(panel === 'comments')}
    >
      {commentControlLabel(count)}
      {count > 0 && (
        <Box ml='6px' boxSize='7px' borderRadius='full' bg={accent.solid} aria-hidden />
      )}
    </Flex>
    <Flex
      as='button'
      type='button'
      aria-pressed={panel === 'activity'}
      aria-label='Activity'
      onClick={() => onPanel('activity')}
      {...stripButton(panel === 'activity')}
    >
      Activity
    </Flex>
  </Flex>
);

const ConversationPanel = ({
  comments,
  activity,
  panel,
  onAdd,
  viewer,
  people = {},
  busy = false,
}: {
  comments: TaskComment[];
  activity: ActivityEvent[];
  panel: 'comments' | 'activity';
  onAdd: (body: string) => Promise<void>;
  viewer?: TasksViewer;
  people?: Record<number, string>;
  busy?: boolean;
}) => {
  const [draft, setDraft] = useState('');
  const [problem, setProblem] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setProblem('');
    setSending(true);
    try {
      await onAdd(body);
      setDraft('');
    } catch (commentError: unknown) {
      setProblem(
        commentError instanceof Error && commentError.message
          ? commentError.message
          : 'That comment could not be added.',
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Flex
      as='aside'
      aria-label={panel === 'comments' ? 'Comments' : 'Activity'}
      direction='column'
      flex='1'
      minH={0}
      minW={0}
    >
      {panel === 'comments' && (
        <Box>
          <Text fontSize={fontSize.label} fontWeight={600} color={text.secondary}>
            Comments
          </Text>
          {comments.length === 0 && (
            <Text mt={3} fontSize={fontSize.body} color={text.secondary}>
              No comments yet.
            </Text>
          )}
          <Flex direction='column' gap={4} mt={comments.length > 0 ? 4 : 0}>
            {comments.map((comment) => (
              <Box key={comment.id}>
                <Text fontSize={fontSize.label} color={text.secondary}>
                  {personName(comment.author_user_id, viewer, people)} ·{' '}
                  {formatWhen(comment.created_at)}
                </Text>
                <Text mt={1} fontSize={fontSize.body} color={text.primary}>
                  {comment.body}
                </Text>
              </Box>
            ))}
          </Flex>
          <Textarea
            mt={4}
            aria-label='Add a comment'
            placeholder='Write a comment'
            value={draft}
            {...taskFieldProperties}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                event.preventDefault();
                send();
              }
            }}
          />
          {problem && (
            <Text mt={2} fontSize={fontSize.body} color={text.primary} role='alert'>
              {problem}
            </Text>
          )}
          <Flex justify='flex-end' mt={2}>
            <Button
              size='sm'
              {...taskPrimaryButtonProperties}
              isDisabled={busy || sending || draft.trim() === ''}
              onClick={send}
            >
              Add comment
            </Button>
          </Flex>
        </Box>
      )}
      {panel === 'activity' && (
        <Box flex='1'>
          <Text fontSize={fontSize.label} fontWeight={600} color={text.secondary}>
            Activity
          </Text>
          {activity.length === 0 && (
            <Text mt={3} fontSize={fontSize.body} color={text.secondary}>
              Nothing has been recorded on this task yet.
            </Text>
          )}
          <Flex direction='column' gap={3} mt={activity.length > 0 ? 4 : 0}>
            {activity.map((event) => {
              const note = typeof event.metadata.note === 'string' ? event.metadata.note : '';
              return (
                <Box key={event.id}>
                  <Text fontSize={fontSize.body} color={text.primary}>
                    {ACTIVITY_LABEL[event.event_type] ?? event.event_type}
                  </Text>
                  <Text fontSize={fontSize.label} color={text.secondary}>
                    {personName(event.actor_user_id, viewer, people)} ·{' '}
                    {formatWhen(event.created_at)}
                  </Text>
                  {note && (
                    <Text mt={1} fontSize={fontSize.body} color={text.secondary}>
                      {note}
                    </Text>
                  )}
                </Box>
              );
            })}
          </Flex>
        </Box>
      )}
    </Flex>
  );
};

export default ConversationPanel;
